import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, initFlatpickrInShadow, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import flatpickr from 'flatpickr';

export class TraderverseMarketWinLossSector {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private paginationState = {
        currentPage: 1,
        itemsPerPage: 10,
        totalPages: 1,
        totalItems: 0
    };
    
    // Filter state — default to last 1 year ending today
    private filters = (() => {
        const end = new Date();
        const start = new Date(end);
        start.setFullYear(start.getFullYear() - 1);
        return {
            groupBy: 'sector',
            p_start_date: start.toISOString().split('T')[0],
            p_end_date: end.toISOString().split('T')[0],
            p_min_threshold: 5,
            p_sort_by: 'losers',
        };
    })();

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
      headerPinned: true,
            type: "Cash and Debt",
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            period: 'Yearly'
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private chartInstance: any = null;
    private legendStates = [true, true];

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';
        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Winners/Losers by Sector </h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <div class="legendsCollection">
                            <span class="toggle-series active" style="background-color:#00FF57"></span>
                            <span class="toggle-series active" style="background-color:#FF0000"></span>
                        </div>
                    </div>
                    <span></span>
                    <div class="filtersBox sideBar hidden">
                        <div class="item">
                            <label>Group By:</label>
                            <select class="filter-groupBy">
                                <option value="sector">Sector</option>
                                <option value="industry">Industry</option>
                                <option value="exchange">Exchange</option>
                            </select>
                        </div>
                        <div class="item">
                            <label>Date Range:</label>
                            <input type="text" class="filter-date-range" value="${this.filters.p_start_date} to ${this.filters.p_end_date}" placeholder="Select date range" readonly>
                        </div>
                        <div class="item">
                            <label>Min % Threshold:</label>
                            <input type="number" class="filter-min-threshold" min="0" max="100" value="${this.filters.p_min_threshold}">
                        </div>
                        <div class="item">
                            <label>Sort By:</label>
                            <select class="filter-sort-by">
                                <option value="winners">Winners</option>
                                <option value="losers">Losers</option>
                            </select>
                        </div>
                    </div>
                </div>
                
                <div class="chartDv" id="sourceReliability">
                    <div id="chartContainer" class="chart coverDv"></div>
                </div>
                
                <!-- Pagination Controls -->
                <div class="market-winloss-pagination-controls-row hidden">
                    <div class="pagination-info">
                        Showing <span class="pagination-range"></span> of <span class="pagination-total"></span> items
                    </div>
                    <div class="pagination-buttons">
                        <button class="pagination-btn first" title="First Page">««</button>
                        <button class="pagination-btn prev" title="Previous Page">«</button>
                        <span class="pagination-current">Page 1 of 1</span>
                        <button class="pagination-btn next" title="Next Page">»</button>
                        <button class="pagination-btn last" title="Last Page">»»</button>
                    </div>
                </div>
            </div>
        </div>
        `;

        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        
        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', () => {
                filtersBox.classList.toggle('hidden');
            });
        }

        const legendEls = this.shadowRoot.querySelectorAll('.legendsCollection .toggle-series');
        legendEls.forEach((el, idx) => {
            el.addEventListener('click', () => {
                this.legendStates[idx] = !this.legendStates[idx];
                el.classList.toggle('active', this.legendStates[idx]);
                this.toggleChartSeries(idx, this.legendStates[idx]);
            });
        });

        const groupByEl = this.shadowRoot.querySelector('.filter-groupBy') as HTMLSelectElement;
        const dateRangeEl = this.shadowRoot.querySelector('.filter-date-range') as HTMLInputElement;
        const minThresholdEl = this.shadowRoot.querySelector('.filter-min-threshold') as HTMLInputElement;
        const sortByEl = this.shadowRoot.querySelector('.filter-sort-by') as HTMLSelectElement;

        const updateFiltersAndFetch = () => {
            const groupBy = groupByEl.value;
            let p_start_date = this.filters.p_start_date;
            let p_end_date = this.filters.p_end_date;
            
            if (dateRangeEl.value.includes(' to ')) {
                const [start, end] = dateRangeEl.value.split(' to ');
                p_start_date = start;
                p_end_date = end;
            }
            
            const p_min_threshold = parseFloat(minThresholdEl.value);
            const p_sort_by = sortByEl.value;
            
            this.filters = { groupBy, p_start_date, p_end_date, p_min_threshold, p_sort_by };
            this.paginationState.currentPage = 1;
            this.setupChart(this.config.symbol!);
        };

        groupByEl.addEventListener('change', updateFiltersAndFetch);
        minThresholdEl.addEventListener('change', updateFiltersAndFetch);
        sortByEl.addEventListener('change', updateFiltersAndFetch);

        // Setup pagination button listeners
        this.setupPaginationListeners();

        // Initialize flatpickr for date range picker
        if (typeof flatpickr !== 'undefined') {
            initFlatpickrInShadow(dateRangeEl, this.shadowRoot, {
                mode: 'range',
                dateFormat: 'Y-m-d',
                defaultDate: [this.filters.p_start_date, this.filters.p_end_date],
                onChange: (selectedDates, dateStr) => {
                    if (selectedDates.length === 2) {
                        const start = flatpickr.formatDate(selectedDates[0], 'Y-m-d');
                        const end = flatpickr.formatDate(selectedDates[1], 'Y-m-d');
                        dateRangeEl.value = `${start} to ${end}`;
                        updateFiltersAndFetch();
                    }
                }
            });
        }

        // Set initial filter values
        (this.shadowRoot.querySelector('.filter-groupBy') as HTMLSelectElement).value = this.filters.groupBy;
        (this.shadowRoot.querySelector('.filter-sort-by') as HTMLSelectElement).value = this.filters.p_sort_by;
    }

    private setupPaginationListeners(): void {
        const prevBtn = this.shadowRoot.querySelector('.pagination-btn.prev') as HTMLButtonElement;
        const nextBtn = this.shadowRoot.querySelector('.pagination-btn.next') as HTMLButtonElement;
        const firstBtn = this.shadowRoot.querySelector('.pagination-btn.first') as HTMLButtonElement;
        const lastBtn = this.shadowRoot.querySelector('.pagination-btn.last') as HTMLButtonElement;

        if (prevBtn) {
            prevBtn.addEventListener('click', () => this.goToPage(this.paginationState.currentPage - 1));
        }
        
        if (nextBtn) {
            nextBtn.addEventListener('click', () => this.goToPage(this.paginationState.currentPage + 1));
        }
        
        if (firstBtn) {
            firstBtn.addEventListener('click', () => this.goToPage(1));
        }
        
        if (lastBtn) {
            lastBtn.addEventListener('click', () => this.goToPage(this.paginationState.totalPages));
        }
    }

    private goToPage(page: number): void {
        if (page < 1 || page > this.paginationState.totalPages) return;
        
        this.paginationState.currentPage = page;
        this.updateDisplay();
        this.updatePaginationUI();
    }

    private updatePaginationUI(): void {
        const paginationControls = this.shadowRoot.querySelector('.market-winloss-pagination-controls-row') as HTMLElement;
        if (!paginationControls) return;

        const { currentPage, totalPages, itemsPerPage, totalItems } = this.paginationState;
        
        // Update current page display
        const currentPageEl = this.shadowRoot.querySelector('.pagination-current') as HTMLElement;
        if (currentPageEl) {
            currentPageEl.textContent = `Page ${currentPage} of ${totalPages}`;
        }
        
        // Update range display
        const rangeEl = this.shadowRoot.querySelector('.pagination-range') as HTMLElement;
        const totalEl = this.shadowRoot.querySelector('.pagination-total') as HTMLElement;
        
        if (rangeEl && totalEl) {
            const startIndex = (currentPage - 1) * itemsPerPage + 1;
            const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
            rangeEl.textContent = `${startIndex}-${endIndex}`;
            totalEl.textContent = totalItems.toString();
        }
        
        // Update button states
        const prevBtn = this.shadowRoot.querySelector('.pagination-btn.prev') as HTMLButtonElement;
        const nextBtn = this.shadowRoot.querySelector('.pagination-btn.next') as HTMLButtonElement;
        const firstBtn = this.shadowRoot.querySelector('.pagination-btn.first') as HTMLButtonElement;
        const lastBtn = this.shadowRoot.querySelector('.pagination-btn.last') as HTMLButtonElement;
        
        if (prevBtn) prevBtn.disabled = currentPage === 1;
        if (nextBtn) nextBtn.disabled = currentPage === totalPages;
        if (firstBtn) firstBtn.disabled = currentPage === 1;
        if (lastBtn) lastBtn.disabled = currentPage === totalPages;
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            const { groupBy, p_start_date, p_end_date, p_min_threshold, p_sort_by } = this.filters;
            const url = `${APIURL}/v3/discover/winner-losers/${groupBy}?p_start_date=${p_start_date}&p_end_date=${p_end_date}&p_min_threshold=${p_min_threshold}&p_sort_by=${p_sort_by}`;
            const res = await apiGet({
                url,
                method: "GET",
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.currentData = json.data?.data ?? [];
            
            // Update pagination state
            this.paginationState.totalItems = this.currentData.length;
            this.paginationState.totalPages = Math.ceil(this.currentData.length / this.paginationState.itemsPerPage);
            this.paginationState.currentPage = Math.min(this.paginationState.currentPage, this.paginationState.totalPages);
            
            // Show/hide pagination controls based on data size
            const paginationControls = this.shadowRoot.querySelector('.market-winloss-pagination-controls-row') as HTMLElement;
            if (paginationControls) {
                if (this.currentData.length > this.paginationState.itemsPerPage) {
                    paginationControls.classList.remove('hidden');
                } else {
                    paginationControls.classList.add('hidden');
                }
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

    private updateDisplay(): void {
        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
            const containerWrap = this.shadowRoot.querySelector('#sourceReliability') as HTMLElement;

    if (this.currentData && Array.isArray(this.currentData) && this.currentData.length > 0) {
        containerWrap?.setAttribute('data-testing-id', 'Market Win Loss Sector');
    } else {
        containerWrap?.removeAttribute('data-testing-id');
    }

        this.renderChartWithPagination();
        this.updatePaginationUI();
    }

    private getPaginatedData(): any[] {
        const { currentPage, itemsPerPage } = this.paginationState;
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return this.currentData.slice(startIndex, endIndex);
    }

    private renderChartWithPagination(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const paginatedData = this.getPaginatedData();
        
        if (!paginatedData || paginatedData.length === 0) {
            container.innerHTML = getNoDataHTML();
            this.chartInstance = null;
            return;
        }

        const yAxisData = paginatedData.map((item: any) => item.sector || item.industry || item.exchange);
        const gainersData = paginatedData.map((item: any) => item.gainers ?? 0);
        const losersData = paginatedData.map((item: any) => item.losers ?? 0);
        const gainersPct = paginatedData.map((item: any) => item.gainers_pct ?? 0);
        const losersPct = paginatedData.map((item: any) => item.losers_pct ?? 0);

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');
        const barWidth = '8px';

        // Calculate dynamic height based on data count
        const barHeight = 30;
        const minHeight = 300;
        const maxHeight = 600;
        const calculatedHeight = Math.min(Math.max(paginatedData.length * barHeight, minHeight), maxHeight);
        
        // Create scrollable container
        container.innerHTML = `
            <div class="chart-scroll-wrapper" style="height: ${calculatedHeight}px;">
                <div id="chartInner" style="width: 100%; min-height: ${calculatedHeight}px;"></div>
            </div>
        `;

        const chartInner = container.querySelector('#chartInner') as HTMLElement;

        // Reserve left space so long sector/industry labels are not clipped
        const displayLabels = yAxisData.map((v: string) => {
            const label = String(v || '');
            return label.length > 30 ? label.substring(0, 30) + '...' : label;
        });
        const longestLabelChars = displayLabels.reduce((max: number, label: string) => {
            return Math.max(max, label.length);
        }, 0);
        const gridLeft = Math.min(Math.max(Math.ceil(longestLabelChars * 7.5) + 16, 100), 200);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: bgColor,
                textStyle: {
                    color: textColor,
                    fontSize: fontSize
                },
                formatter: (params: any) => {
                    const idx = params[0].dataIndex;
                    return `
                        <b>${yAxisData[idx]}</b><br/>
                        Gainers: ${gainersData[idx]} (${gainersPct[idx]}%)<br/>
                        Losers: ${losersData[idx]} (${losersPct[idx]}%)<br/>
                        Total: ${gainersData[idx] + losersData[idx]}
                    `;
                }
            },
            grid: { left: gridLeft, right: 16, top: 10, bottom: 48, containLabel: false },
            xAxis: {
                type: 'value',
                name: 'Count',
                nameLocation: 'middle',
                nameGap: 40,
                nameTextStyle: { color: textColor },
                axisLabel: { color: textColor, fontSize: fontSize },
                splitLine: { lineStyle: { color: splitLineColor } },
            },
            yAxis: {
                type: 'category',
                data: yAxisData,
                splitLine: { lineStyle: { color: splitLineColor } },
                axisLabel: {
                    color: textColor,
                    fontSize: fontSize,
                    interval: 0,
                    margin: 8,
                    formatter: (v: string) => {
                        // Truncate long labels
                        return v.length > 30 ? v.substring(0, 30) + '...' : v;
                    }
                },
                axisTick: { show: false }
            },
            barCategoryGap: '40%',
            barGap: '30%',
            series: [
                {
                    name: 'Gainers',
                    type: 'bar',
                    data: this.legendStates[0] ? gainersData : [],
                    barWidth: barWidth,
                    itemStyle: {
                        borderRadius: [0, 5, 5, 0],
                        color: '#00FF57'
                    },
                    label: {
                        show: false
                    },
                    large: true,
                    largeThreshold: 100
                },
                {
                    name: 'Losers',
                    type: 'bar',
                    data: this.legendStates[1] ? losersData : [],
                    barWidth: barWidth,
                    itemStyle: {
                        borderRadius: [0, 5, 5, 0],
                        color: '#FF0000'
                    },
                    label: {
                        show: false
                    },
                    large: true,
                    largeThreshold: 100
                }
            ]
        };

        this.chartInstance = echarts.init(chartInner);
        this.chartInstance.setOption(option);

        // Handle resize
        const resizeChart = () => {
            if (this.chartInstance) {
                this.chartInstance.resize();
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            setTimeout(resizeChart, 100);
        });

        resizeObserver.observe(container);
    }

    private toggleChartSeries(seriesIdx: number, visible: boolean): void {
        if (!this.chartInstance) return;
        const option = this.chartInstance.getOption();
        if (!option.series) return;
        
        const paginatedData = this.getPaginatedData();
        const gainersData = paginatedData.map((item: any) => item.gainers ?? 0);
        const losersData = paginatedData.map((item: any) => item.losers ?? 0);
        
        if (seriesIdx === 0) {
            option.series[0].data = visible ? gainersData : [];
        } else if (seriesIdx === 1) {
            option.series[1].data = visible ? losersData : [];
        }
        
        this.chartInstance.setOption(option);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    
    public destroy(): void {
        if (this.updateInterval) { 
            clearInterval(this.updateInterval); 
            this.updateInterval = null; 
        }
        if (this.widgetResizeObserver) { 
            this.widgetResizeObserver.disconnect(); 
            this.widgetResizeObserver = null; 
        }
        this.eventHandlers.clear(); 
        this.shadowRoot.innerHTML = '';
    }
    
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { 
            const evt: WidgetEvent = { type, data }; 
            handlers.forEach(h => h(evt)); 
        }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> 
                    <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> 
                    <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> 
                </svg>`;
    }

}

declare global { 
    interface Window { 
        TraderverseMarketWinLossSector: typeof TraderverseMarketWinLossSector; 
    } 
}

if (typeof window !== 'undefined') { 
    window.TraderverseMarketWinLossSector = TraderverseMarketWinLossSector; 
}
